/**
 * LayoutEngine - 2-Pass Layout Algorithm
 *
 * Pass 1: Measure (bottom-up) - Compute intrinsic sizes for Hug nodes
 * Pass 2: Resolve (top-down) - Resolve final sizes, position children
 *
 * CRITICAL: This file has ZERO Babylon.js imports.
 */

import type { LayoutContainer, LayoutNode, Vector3D, LayoutAxis, LayoutAlignment, MainAlignment, SizeMode } from './types';
import { createVector3D, LAYOUT_EPSILON } from './types';
import { LayoutTree } from './LayoutTree';
import {
  getContainerCrossAxes,
  getAxisValue,
  setAxisValue,
  validateLayoutNode,
  normalizeContainerAlignments,
  normalizeItemAlignments
} from './LayoutRules';

/**
 * Provider interface for getting intrinsic sizes from external sources.
 */
export interface IntrinsicSizeProvider {
  getIntrinsicSize(node: LayoutNode): Vector3D;
  getMinSize(node: LayoutNode): Vector3D;
}

/** Default intrinsic size provider */
export const defaultIntrinsicProvider: IntrinsicSizeProvider = {
  getIntrinsicSize: () => createVector3D(100, 100, 100),
  getMinSize: () => createVector3D(0, 0, 0)
};

/** Line structure for wrapping */
interface LayoutLine {
  children: LayoutNode[];
  mainSize: number;
  crossASize: number;
  crossBSize: number;
}

/** Layer structure for 3D wrapping */
interface LayoutLayer {
  lines: LayoutLine[];
  crossASize: number;
  crossBSize: number;
}

// === CONTENT FIT ===

/** An extent at or below this is not a size to fit against (see `computeFitScale`). */
const FIT_DEGENERATE_EXTENT = 1e-6;

/**
 * The uniform scale a content slot gives its child (LAYOUT_ENGINE.md §5.2).
 *
 * `content` is the child's box at scale 1; `room` is the slot's inner width
 * and height, null on an axis that does not constrain (a Hug slot axis was
 * sized BY the content, so fitting against it is circular). Depth never
 * constrains.
 *
 * With `zooms`, the fit takes the largest step at or under the continuous
 * ratio, and the ratio itself when no step is small enough.
 */
export function computeContentFitScale(
  contentFit: NonNullable<LayoutContainer['contentFit']>,
  content: Vector3D,
  room: { x: number | null; y: number | null },
): number {
  if (contentFit.fit === 'none') return 1;
  const ratios: number[] = [];
  if (room.x != null && room.x > FIT_DEGENERATE_EXTENT && content.x > FIT_DEGENERATE_EXTENT) {
    ratios.push(room.x / content.x);
  }
  if (room.y != null && room.y > FIT_DEGENERATE_EXTENT && content.y > FIT_DEGENERATE_EXTENT) {
    ratios.push(room.y / content.y);
  }
  if (ratios.length === 0) return 1;
  let scale = Math.min(...ratios);
  if (contentFit.fit === 'scale-down') scale = Math.min(scale, 1);
  const steps = (contentFit.zooms ?? []).filter(step => step > 0 && step <= scale * (1 + LAYOUT_EPSILON));
  if (steps.length > 0) scale = Math.max(...steps);
  return scale;
}

// === SHARED FLEX DISTRIBUTION ===

interface FlexChild {
  id: string;
  baseMain: number;
  grow: number;
  shrink: number;
  minMain: number;
  maxMain: number;
}

/**
 * Compute the minimum size for a flex item along the main axis.
 * CSS min-height:auto behavior.
 */
function computeFlexMin(
  mainMode: SizeMode,
  explicitMin: number,
  measuredMain: number,
  minExplicit: boolean,
  hasClipContent: boolean,
  hasMeasuredContent = true
): number {
  const contentFloor = hasMeasuredContent ? measuredMain : 0;
  if (mainMode === 'Hug') {
    return Math.max(explicitMin, contentFloor);
  }
  if (mainMode === 'Fixed') {
    return explicitMin;
  }
  // Fill:
  if (minExplicit || hasClipContent) {
    return explicitMin;
  }
  return Math.max(explicitMin, contentFloor);
}

/**
 * Distribute available space among flex children using grow/shrink weights.
 * Implements CSS Flexbox distribution with iterative freezing.
 */
function distributeFlex(
  flexes: FlexChild[],
  availableMain: number,
  gap: number
): Map<string, number> {
  const totalBase = flexes.reduce((sum, f) => sum + f.baseMain, 0) +
    Math.max(0, flexes.length - 1) * gap;
  const freeSpace = availableMain - totalBase;

  const targetMain = new Map<string, number>();
  for (const f of flexes) targetMain.set(f.id, f.baseMain);

  if (freeSpace > 0) {
    // GROW
    const growable = new Set(flexes.filter(f => f.grow > 0).map(f => f.id));

    for (let round = 0; round < flexes.length + 1; round++) {
      let frozenTotal = 0;
      for (const f of flexes) {
        if (!growable.has(f.id)) frozenTotal += (targetMain.get(f.id) ?? f.baseMain);
        else frozenTotal += f.baseMain;
      }
      const surplus = availableMain - frozenTotal - Math.max(0, flexes.length - 1) * gap;
      if (surplus <= LAYOUT_EPSILON) break;

      const active = flexes.filter(f => growable.has(f.id));
      const sumGrow = active.reduce((sum, f) => sum + f.grow, 0);
      if (sumGrow <= 0) break;

      let anyFrozen = false;
      for (const f of active) {
        const share = f.grow / sumGrow;
        const hypothetical = f.baseMain + surplus * share;

        if (hypothetical < f.minMain - LAYOUT_EPSILON) {
          targetMain.set(f.id, f.minMain);
          growable.delete(f.id);
          anyFrozen = true;
        } else if (hypothetical > f.maxMain + LAYOUT_EPSILON) {
          targetMain.set(f.id, f.maxMain);
          growable.delete(f.id);
          anyFrozen = true;
        } else {
          targetMain.set(f.id, hypothetical);
        }
      }

      if (!anyFrozen) break;
    }
  } else if (freeSpace < 0) {
    // SHRINK
    let overflow = -freeSpace;
    const shrinkable = new Set(
      flexes.filter(f => f.shrink > 0 && f.baseMain > 0).map(f => f.id)
    );

    while (overflow > LAYOUT_EPSILON && shrinkable.size > 0) {
      const active = flexes.filter(f => shrinkable.has(f.id));
      const sumScaledShrink = active.reduce((sum, f) => sum + f.shrink * f.baseMain, 0);
      if (sumScaledShrink <= 0) break;

      let consumed = 0;
      for (const f of active) {
        const current = targetMain.get(f.id) ?? f.baseMain;
        const share = (f.shrink * f.baseMain) / sumScaledShrink;
        const reduce = overflow * share;
        const next = Math.max(f.minMain, current - reduce);
        consumed += (current - next);
        targetMain.set(f.id, next);
        if (next <= f.minMain + LAYOUT_EPSILON) {
          shrinkable.delete(f.id);
        }
      }
      if (consumed <= LAYOUT_EPSILON) break;
      overflow = Math.max(0, overflow - consumed);
    }
  }

  return targetMain;
}

/** The main-axis size a flex child ends up with: its distributed target held to its own minimum and maximum. */
function clampFlexMain(target: number, minMain: number, maxMain: number): number {
  return Math.min(maxMain, Math.max(minMain, target));
}

export class LayoutEngine {
  private intrinsicProvider: IntrinsicSizeProvider = defaultIntrinsicProvider;

  /**
   * A wrapped container asked an ancestor how much room it has and the
   * ancestor could not say — it Hugs, and its own measure has not run yet —
   * or counted a Hug sibling of its share that has not been measured yet.
   * Set inside `deriveConstraintOuter` (and `flowShareOf`), read by
   * `computeLayout` to decide whether a second measure/resolve cycle is worth
   * running.
   */
  private constraintWasIndefinite = false;

  private getRelativeAxisPercent(child: LayoutNode, axis: LayoutAxis): number | null {
    const item = child.layoutItem;
    if (!item) return null;
    const percent = axis === 'X'
      ? item.widthPercent
      : axis === 'Y'
        ? item.heightPercent
        : undefined;
    return typeof percent === 'number' && Number.isFinite(percent) ? percent : null;
  }

  /**
   * Whether `layoutBox.size` on this axis is a MEASUREMENT, and so a valid
   * `min-height:auto` content floor.
   *
   * Two ways it is not. An empty container measured nothing. And a size the
   * DATA authored on a Fill axis is a flex-basis, not content — the measure
   * pass bakes back on Hug axes only, so nothing measured it. Reading an
   * authored basis as a content floor froze the item at that number instead
   * of letting it collapse: a card 0.96 m tall held its body slot 1.5 m open.
   *
   * The basis is still on the box, because line breaking and a Hug ancestor
   * both read `layoutBox.size` directly and a Fill child has to wrap on it.
   */
  private hasMeasuredContentFloor(child: LayoutNode, mainAxis: LayoutAxis): boolean {
    if (child.layoutContainer && child.childrenIds.length === 0) return false;
    const box = child.layoutBox;
    const authored = mainAxis === 'X' ? box.sizeIsBasisX
      : mainAxis === 'Y' ? box.sizeIsBasisY : box.sizeIsBasisZ;
    return !authored;
  }

  private isAbsolute(child: LayoutNode): boolean {
    return child.layoutItem?.positionMode === 'Absolute';
  }

  private getOrderedChildren(tree: LayoutTree, parentId: string): LayoutNode[] {
    const children = tree.getChildren(parentId);
    if (children.length <= 1) return children;

    const orders = children.map((c) => c.layoutItem?.order ?? 0);
    const hasNonZero = orders.some((o) => o !== 0);
    if (!hasNonZero) return children;

    return children
      .map((c, i) => ({ c, i, order: c.layoutItem?.order ?? 0 }))
      .sort((a, b) => (a.order - b.order) || (a.i - b.i))
      .map((x) => x.c);
  }

  /** Set the intrinsic size provider. */
  setIntrinsicProvider(provider: IntrinsicSizeProvider): void {
    this.intrinsicProvider = provider;
  }

  /** Compute layout for the entire tree. */
  computeLayout(tree: LayoutTree): void {
    this.normalizeAlignments(tree);

    // Pre-pass: Derive mainAxisMode from parent's direction axis.
    // mainAxisMode is "the child's sizeMode along the parent's main axis" —
    // a derived value the engine owns, not something callers should compute.
    this.resolveMainAxisModes(tree);

    // Measure (bottom-up) then Resolve (top-down), at most twice.
    //
    // A wrapping container has to know its available extent before it can
    // choose line breaks, and it derives that by walking up the tree. Measure
    // is bottom-up, so on the first cycle every Hug ancestor is still
    // unmeasured and answers "indefinite" — the container measures at
    // max-content, one line, which is exactly how CSS measures a wrap child
    // while sizing its shrink-to-fit parent. Resolve then hands those
    // ancestors real sizes, so a second cycle re-measures the breaks against
    // them.
    //
    // Two cycles, not a loop to a fixed point: cycle 2 reads `resolvedSize`,
    // and any further cycle would read the same `resolvedSize` back and
    // reproduce itself. Pinned by the "a second solve changes nothing" test
    // in `test/vitest/layout/hug-ancestor-fill.test.ts` — which fails on
    // exactly this tree before the change, so the engine is *more* settled
    // after it, not less.
    //
    // Trees with no wrapping container never set the flag and take one cycle,
    // as they always have.
    for (let cycle = 0; cycle < 2; cycle++) {
      this.constraintWasIndefinite = false;

      tree.traverseBottomUp(node => this.measure(node, tree));

      for (const root of tree.getRoots()) {
        root.resolvedPosition = { ...root.position };
        this.resolve(root, tree, false);
      }

      if (!this.constraintWasIndefinite) break;
    }

    // Validate all nodes
    tree.traverse(node => validateLayoutNode(node));
  }

  /** Make every authored alignment one the offset helpers know (see normalizeAlignment). */
  private normalizeAlignments(tree: LayoutTree): void {
    for (const node of tree.getAllNodes()) {
      if (node.layoutContainer) normalizeContainerAlignments(node.layoutContainer);
      if (node.layoutItem) normalizeItemAlignments(node.layoutItem);
    }
  }

  private resolveMainAxisModes(tree: LayoutTree): void {
    for (const node of tree.getAllNodes()) {
      if (!node.parentId || !node.layoutItem) continue;
      const parent = tree.getNode(node.parentId);
      if (!parent?.layoutContainer) continue;
      const axis = parent.layoutContainer.directionAxis;
      const key = axis === 'X' ? 'x' : axis === 'Y' ? 'y' : 'z';
      node.layoutItem.mainAxisMode = node.layoutBox.sizeMode[key];
    }
  }

  // === PASS 1: MEASURE ===

  private measure(node: LayoutNode, tree: LayoutTree): void {
    const children = this.getOrderedChildren(tree, node.id);

    if (node.layoutContainer && children.length > 0) {
      this.measureContainer(node, children, tree);
    } else if (node.layoutContainer) {
      // Empty container: Hug axes collapse to padding only (no content)
      this.measureEmptyContainer(node);
    } else {
      this.measureLeaf(node);
    }
  }

  private measureEmptyContainer(node: LayoutNode): void {
    const container = node.layoutContainer!;
    const box = node.layoutBox;
    const padding = container.padding;

    if (box.sizeMode.x === 'Hug') box.size.x = padding.left + padding.right;
    if (box.sizeMode.y === 'Hug') box.size.y = padding.top + padding.bottom;
    if (box.sizeMode.z === 'Hug') box.size.z = padding.front + padding.back;
  }

  private measureLeaf(node: LayoutNode): void {
    const intrinsic = this.intrinsicProvider.getIntrinsicSize(node);
    const min = this.intrinsicProvider.getMinSize(node);

    node.layoutBox.min = {
      x: Math.max(node.layoutBox.min.x, min.x),
      y: Math.max(node.layoutBox.min.y, min.y),
      z: Math.max(node.layoutBox.min.z, min.z)
    };

    const box = node.layoutBox;
    if (box.sizeMode.x === 'Hug') box.size.x = intrinsic.x;
    if (box.sizeMode.y === 'Hug') box.size.y = intrinsic.y;
    if (box.sizeMode.z === 'Hug') box.size.z = intrinsic.z;
  }

  private measureContainer(node: LayoutNode, children: LayoutNode[], tree: LayoutTree): void {
    const container = node.layoutContainer!;
    const box = node.layoutBox;
    const [crossA, crossB] = getContainerCrossAxes(container);

    const flowChildren = children.filter(c => !this.isAbsolute(c));

    if (container.wrap === true) {
      this.measureWrappedContainer(node, flowChildren, tree, container.directionAxis, crossA, crossB);
      return;
    }

    // A content slot measures its child as the fit will show it, so a Hug
    // depth follows the fitted content. The room is the slot's Fixed axes
    // only: a Fill share is not known until resolve.
    const fitted = this.fittedChild(node, flowChildren);
    if (fitted) {
      const padding = container.padding;
      fitted.flowScale = computeContentFitScale(container.contentFit!, fitted.layoutBox.size, {
        x: box.sizeMode.x === 'Fixed' ? Math.max(0, box.size.x - padding.left - padding.right) : null,
        y: box.sizeMode.y === 'Fixed' ? Math.max(0, box.size.y - padding.top - padding.bottom) : null,
      });
    }

    let mainSize = 0;
    let crossASize = 0;
    let crossBSize = 0;

    for (let i = 0; i < flowChildren.length; i++) {
      const child = flowChildren[i];
      const childSize = this.flowBox(child, child.layoutBox.size);

      mainSize += getAxisValue(childSize, container.directionAxis);
      if (i > 0) mainSize += container.gap;

      crossASize = Math.max(crossASize, getAxisValue(childSize, crossA));
      crossBSize = Math.max(crossBSize, getAxisValue(childSize, crossB));
    }

    const padding = container.padding;
    const mainPadding = this.getMainPadding(container.directionAxis, padding);
    const crossAPadding = this.getMainPadding(crossA, padding);
    const crossBPadding = this.getMainPadding(crossB, padding);

    mainSize += mainPadding;
    crossASize += crossAPadding;
    crossBSize += crossBPadding;

    if (box.sizeMode.x === 'Hug') {
      if (container.directionAxis === 'X') box.size.x = mainSize;
      else if (crossA === 'X') box.size.x = crossASize;
      else box.size.x = crossBSize;
    }
    if (box.sizeMode.y === 'Hug') {
      if (container.directionAxis === 'Y') box.size.y = mainSize;
      else if (crossA === 'Y') box.size.y = crossASize;
      else box.size.y = crossBSize;
    }
    if (box.sizeMode.z === 'Hug') {
      if (container.directionAxis === 'Z') box.size.z = mainSize;
      else if (crossA === 'Z') box.size.z = crossASize;
      else box.size.z = crossBSize;
    }
  }

  /**
   * The flex inputs of one container's flowed children on its main axis: what
   * the resolve pass distributes, and what a wrap measure uses to find a Fill
   * child's share (`flowShareOf`), so the two cannot disagree.
   */
  private buildFlexInputs(
    children: LayoutNode[],
    mainAxis: LayoutAxis,
    availableMain: number
  ): { flexes: FlexChild[]; childFlexMap: Map<string, { child: LayoutNode; minMain: number; maxMain: number }> } {
    const axisKey = mainAxis.toLowerCase() as 'x' | 'y' | 'z';
    const childFlexMap = new Map<string, { child: LayoutNode; minMain: number; maxMain: number }>();
    const flexes: FlexChild[] = [];

    for (const child of children) {
      const item = child.layoutItem;
      const box = child.layoutBox;
      const relativeMainPercent = this.getRelativeAxisPercent(child, mainAxis);
      const mainMode = relativeMainPercent != null ? 'Fixed' : (item?.mainAxisMode ?? box.sizeMode[axisKey]);

      const basisOverride = item?.flexBasis ?? null;
      const measuredMain =
        relativeMainPercent != null
          ? (availableMain * relativeMainPercent) / 100
          : getAxisValue(box.size, mainAxis);
      const baseMain =
        relativeMainPercent != null
          ? measuredMain
          : (basisOverride != null ? basisOverride : (mainMode === 'Fill' ? 0 : measuredMain));
      const grow = item?.flexGrow ?? (mainMode === 'Fill' ? 1 : 0);
      const shrink = item?.flexShrink ?? 1;

      const explicitMin = getAxisValue(box.min, mainAxis);
      const minExplicit = mainAxis === 'X' ? box.minExplicitX : mainAxis === 'Y' ? box.minExplicitY : box.minExplicitZ;
      const hasClipContent = child.layoutContainer?.clipContent === true;
      const minMain = computeFlexMin(
        mainMode, explicitMin, measuredMain, !!minExplicit, hasClipContent,
        this.hasMeasuredContentFloor(child, mainAxis)
      );
      const maxMain = getAxisValue(box.max, mainAxis);

      flexes.push({
        id: child.id, baseMain, grow: Math.max(0, grow), shrink: Math.max(0, shrink), minMain, maxMain
      });
      childFlexMap.set(child.id, { child, minMain, maxMain });
    }
    return { flexes, childFlexMap };
  }

  /** True when `child` is a flowed child of an unwrapped `parent` whose main axis is `axis`. */
  private isUnwrappedFlowChild(parent: LayoutNode, child: LayoutNode, axis: LayoutAxis): boolean {
    const c = parent.layoutContainer;
    if (!c || c.wrap || c.directionAxis !== axis) return false;
    return !this.isAbsolute(child);
  }

  /**
   * The main-axis room one Fill child of an unwrapped container gets from
   * `inner`: the resolve pass's own flex inputs, distribution and clamp.
   *
   * Measure runs bottom-up in child order, so a Hug sibling listed after the
   * row has not been measured yet and still carries its authored size, an
   * inert placeholder. The share is then wrong in either direction, so a Hug
   * sibling on this axis asks for the second cycle, which reads the size the
   * first one measured.
   */
  private flowShareOf(
    tree: LayoutTree,
    parent: LayoutNode,
    child: LayoutNode,
    axis: LayoutAxis,
    inner: number
  ): number {
    const flow = tree.getChildren(parent.id).filter((n) => !this.isAbsolute(n));
    const axisKey = axis.toLowerCase() as 'x' | 'y' | 'z';
    for (const sibling of flow) {
      if (sibling !== child && sibling.layoutBox.sizeMode[axisKey] === 'Hug' && this.getRelativeAxisPercent(sibling, axis) == null) {
        this.constraintWasIndefinite = true;
      }
    }
    const { flexes, childFlexMap } = this.buildFlexInputs(flow, axis, inner);
    const target = distributeFlex(flexes, inner, parent.layoutContainer!.gap).get(child.id);
    const entry = childFlexMap.get(child.id);
    if (target === undefined || !entry) return inner;
    return clampFlexMain(target, entry.minMain, entry.maxMain);
  }

  private measureWrappedContainer(
    node: LayoutNode,
    children: LayoutNode[],
    tree: LayoutTree,
    mainAxis: LayoutAxis,
    crossA: LayoutAxis,
    crossB: LayoutAxis
  ): void {
    const container = node.layoutContainer!;
    const box = node.layoutBox;
    const padding = container.padding;

    const axisKey = (a: LayoutAxis) => a.toLowerCase() as 'x' | 'y' | 'z';
    const isHug = (a: LayoutAxis) => box.sizeMode[axisKey(a)] === 'Hug';
    const isFill = (a: LayoutAxis) => box.sizeMode[axisKey(a)] === 'Fill';

    const deriveConstraintOuter = (leaf: LayoutNode, axis: LayoutAxis): number | null => {
      if (!leaf.parentId) return null;

      const chain: LayoutNode[] = [];
      let cur: LayoutNode | null = leaf;
      while (cur) {
        chain.push(cur);
        if (!cur.parentId) break;
        const up: LayoutNode | null = tree.getNode(cur.parentId) ?? null;
        // Content in a slot measures at its own size and is scaled after;
        // the slot's width is in the slot's units, not the content's.
        if (up?.layoutContainer?.contentFit) break;
        cur = up;
      }

      const key = axisKey(axis);
      const axisValue = (v: Vector3D) => (axis === 'X' ? v.x : axis === 'Y' ? v.y : v.z);

      const outerOf = (n: LayoutNode): number | null => {
        const rs = axisValue(n.resolvedSize ?? { x: 0, y: 0, z: 0 });
        if (rs > 0.0001) return rs;
        const mode = n.layoutBox.sizeMode[key];
        // A Hug node's authored `size` is NOT a constraint. `width`/`height`/
        // `depth` are `@default 1`, so an unstated Hug axis carries an inert
        // metre until the measure pass overwrites it from content — and
        // measure runs bottom-up, so every ancestor reached from here is
        // still carrying it. Accepting it broke lines against a metre the
        // ancestor did not have. Only Fixed (authored intent) and a size a
        // completed pass already resolved are definite.
        //
        // One comparison for both arms: `0.0001` is this file's existing
        // "an authored size is really there" tolerance (six other sites use
        // it), and splitting the old single guard into two must not introduce
        // a second number for the same question.
        const authored = axisValue(n.layoutBox.size);
        const authoredIsStated = authored > 0.0001;
        if (mode === 'Fixed' && authoredIsStated) return authored;
        if (mode === 'Hug' && authoredIsStated) this.constraintWasIndefinite = true;
        return null;
      };

      let anchorIdx = -1;
      let outer = 0;
      for (let i = 1; i < chain.length; i++) {
        const w = outerOf(chain[i]);
        if (w != null && w > 0) {
          anchorIdx = i;
          outer = w;
          break;
        }
      }
      if (anchorIdx === -1) return null;

      for (let idx = anchorIdx; idx > 0; idx--) {
        const parent = chain[idx];
        const child = chain[idx - 1];

        let inner = outer;
        if (parent.layoutContainer) {
          const pad = parent.layoutContainer.padding;
          if (axis === 'X') inner = Math.max(0, inner - (pad.left + pad.right));
          if (axis === 'Y') inner = Math.max(0, inner - (pad.top + pad.bottom));
          if (axis === 'Z') inner = Math.max(0, inner - (pad.front + pad.back));
        }

        // Same rule on the way down: only a Fixed extent narrows the
        // constraint. An intermediate Hug node is still unmeasured this
        // cycle (see outerOf), so clamping to its authored metre put the
        // placeholder back one level below where outerOf just rejected it.
        const childMode = child.layoutBox.sizeMode[key];
        const childSize = axisValue(child.layoutBox.size);
        // Same single comparison as `outerOf` above, for the same reason.
        const childSizeIsStated = childSize > 0.0001;
        if (childMode === 'Fixed' && childSizeIsStated) {
          outer = Math.min(inner, childSize);
        } else if (childMode === 'Fill' && this.isUnwrappedFlowChild(parent, child, axis)) {
          // A Fill child of an unwrapped container on its own main axis gets
          // a share of the row, not the whole row: siblings take their
          // extent and the Fill ones split what is left.
          outer = this.flowShareOf(tree, parent, child, axis, inner);
        } else {
          if (childMode === 'Hug' && childSizeIsStated) this.constraintWasIndefinite = true;
          outer = inner;
        }
      }

      return outer > 0 ? outer : null;
    };

    const innerSize: Vector3D = {
      x: isHug('X')
        ? Infinity
        : isFill('X')
          ? Math.max(0, (deriveConstraintOuter(node, 'X') ?? Infinity) - padding.left - padding.right)
          : Math.max(0, box.size.x - padding.left - padding.right),
      y: isHug('Y')
        ? Infinity
        : isFill('Y')
          ? Math.max(0, (deriveConstraintOuter(node, 'Y') ?? Infinity) - padding.top - padding.bottom)
          : Math.max(0, box.size.y - padding.top - padding.bottom),
      z: isHug('Z')
        ? Infinity
        : isFill('Z')
          ? Math.max(0, (deriveConstraintOuter(node, 'Z') ?? Infinity) - padding.front - padding.back)
          : Math.max(0, box.size.z - padding.front - padding.back),
    };

    const availableMain = getAxisValue(innerSize, mainAxis);
    const availableCrossA = getAxisValue(innerSize, crossA);
    const gap = container.gap;
    const crossGap = container.crossGap ?? gap;

    type MeasureLine = { mainSize: number; crossASize: number; crossBSize: number };
    type MeasureLayer = { lines: MeasureLine[]; crossASize: number; crossBSize: number };

    const layers: MeasureLayer[] = [];
    let currentLayer: MeasureLayer = { lines: [], crossASize: 0, crossBSize: 0 };
    let currentLine: MeasureLine = { mainSize: 0, crossASize: 0, crossBSize: 0 };

    const commitLineToLayer = () => {
      if (currentLine.mainSize <= 0 && currentLine.crossASize <= 0 && currentLine.crossBSize <= 0) return;

      const nextCrossASize =
        currentLayer.crossASize + (currentLayer.lines.length > 0 ? crossGap : 0) + currentLine.crossASize;

      if (nextCrossASize <= availableCrossA || currentLayer.lines.length === 0) {
        currentLayer.lines.push(currentLine);
        currentLayer.crossASize = nextCrossASize;
        currentLayer.crossBSize = Math.max(currentLayer.crossBSize, currentLine.crossBSize);
      } else {
        layers.push(currentLayer);
        currentLayer = {
          lines: [currentLine],
          crossASize: currentLine.crossASize,
          crossBSize: currentLine.crossBSize,
        };
      }

      currentLine = { mainSize: 0, crossASize: 0, crossBSize: 0 };
    };

    for (const child of children) {
      const childSize = child.layoutBox.size;
      const childMain = getAxisValue(childSize, mainAxis);
      const childCA = getAxisValue(childSize, crossA);
      const childCB = getAxisValue(childSize, crossB);

      const candidateMain =
        currentLine.mainSize + (currentLine.mainSize > 0 ? gap : 0) + childMain;

      if (candidateMain <= availableMain || currentLine.mainSize === 0) {
        currentLine.mainSize = candidateMain;
        currentLine.crossASize = Math.max(currentLine.crossASize, childCA);
        currentLine.crossBSize = Math.max(currentLine.crossBSize, childCB);
      } else {
        commitLineToLayer();
        currentLine.mainSize = childMain;
        currentLine.crossASize = childCA;
        currentLine.crossBSize = childCB;
      }
    }

    if (currentLine.mainSize > 0 || currentLine.crossASize > 0 || currentLine.crossBSize > 0) {
      commitLineToLayer();
    }
    if (currentLayer.lines.length > 0) {
      layers.push(currentLayer);
    }

    let wrappedMain = 0;
    let wrappedCrossA = 0;
    let wrappedCrossB = 0;

    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i];
      wrappedCrossA = Math.max(wrappedCrossA, layer.crossASize);
      wrappedCrossB += layer.crossBSize;
      if (i > 0) wrappedCrossB += crossGap;

      for (const line of layer.lines) {
        wrappedMain = Math.max(wrappedMain, line.mainSize);
      }
    }

    wrappedMain += this.getMainPadding(mainAxis, padding);
    wrappedCrossA += this.getMainPadding(crossA, padding);
    wrappedCrossB += this.getMainPadding(crossB, padding);

    if (box.sizeMode.x === 'Hug') {
      if (mainAxis === 'X') box.size.x = wrappedMain;
      else if (crossA === 'X') box.size.x = wrappedCrossA;
      else box.size.x = wrappedCrossB;
    }
    if (box.sizeMode.y === 'Hug') {
      if (mainAxis === 'Y') box.size.y = wrappedMain;
      else if (crossA === 'Y') box.size.y = wrappedCrossA;
      else box.size.y = wrappedCrossB;
    }
    if (box.sizeMode.z === 'Hug') {
      if (mainAxis === 'Z') box.size.z = wrappedMain;
      else if (crossA === 'Z') box.size.z = wrappedCrossA;
      else box.size.z = wrappedCrossB;
    }
  }

  // === PASS 2: RESOLVE ===

  private resolve(node: LayoutNode, tree: LayoutTree, preserveResolvedSize: boolean): void {
    const sizeToClamp = preserveResolvedSize ? node.resolvedSize : node.layoutBox.size;
    node.resolvedSize = this.clampSize(sizeToClamp, node.layoutBox.min, node.layoutBox.max);

    const children = this.getOrderedChildren(tree, node.id);
    if (children.length === 0) return;

    if (node.layoutContainer) {
      this.resolveContainer(node, children, tree);
    } else {
      for (const child of children) {
        child.resolvedPosition = { ...child.position };
        this.resolve(child, tree, false);
      }
    }
  }

  private resolveContainer(node: LayoutNode, children: LayoutNode[], tree: LayoutTree): void {
    const container = node.layoutContainer!;
    const [crossA, crossB] = getContainerCrossAxes(container);
    const innerSize = this.getInnerSize(node.resolvedSize, container.padding);

    const flowChildren = children.filter(c => !this.isAbsolute(c));
    const absoluteChildren = children.filter(c => this.isAbsolute(c));

    const fitted = this.fittedChild(node, flowChildren);
    if (fitted) {
      container.resolvedWrap = false;
      this.resolveFittedChild(node, fitted, tree, innerSize);
    } else if (flowChildren.length > 0) {
      const shouldWrap = container.wrap === true;
      container.resolvedWrap = shouldWrap;
      if (shouldWrap) {
        this.resolveWrapped(node, flowChildren, tree, innerSize, container.directionAxis, crossA, crossB);
      } else {
        this.resolveNonWrapped(node, flowChildren, tree, innerSize, container.directionAxis, crossA, crossB);
      }
    } else {
      container.resolvedWrap = false;
    }

    if (absoluteChildren.length > 0) {
      // CSS: an absolutely positioned child's containing block is the
      // PADDING box, not the content box — Absolute + Fill spans the whole
      // container (a card body behind padded content), not the inset area.
      //
      // Size and position must agree about which box they are in. We place an
      // Absolute child from `layoutItem.inset` in PADDING-box corner space, so
      // its size resolves against the padding box too. Moving the size to the
      // content box without moving the position with it was measured and made
      // placement worse, not better; change both or neither.
      this.resolveAbsoluteChildren(node, absoluteChildren, tree, node.resolvedSize);
    }
  }

  /**
   * Absolute children are removed from flow. Their POSITION is canonical —
   * the engine copies `node.position` through untouched. Their SIZE still
   * resolves: percent and Fill measure against the containing (padding)
   * box, per CSS; Fixed/Hug use the measured/authored box size.
   */
  private resolveAbsoluteChildren(
    parent: LayoutNode,
    children: LayoutNode[],
    tree: LayoutTree,
    containingSize: Vector3D
  ): void {
    void parent;

    for (const child of children) {
      const box = child.layoutBox;
      const widthPercent = this.getRelativeAxisPercent(child, 'X');
      const heightPercent = this.getRelativeAxisPercent(child, 'Y');

      // X-axis
      if (widthPercent != null) {
        child.resolvedSize.x = containingSize.x * (widthPercent / 100);
      } else if (box.sizeMode.x === 'Fill') {
        child.resolvedSize.x = containingSize.x;
      } else {
        child.resolvedSize.x = box.size.x;
      }

      // Y-axis
      if (heightPercent != null) {
        child.resolvedSize.y = containingSize.y * (heightPercent / 100);
      } else if (box.sizeMode.y === 'Fill') {
        child.resolvedSize.y = containingSize.y;
      } else {
        child.resolvedSize.y = box.size.y;
      }

      // Z-axis — Fill spans the containing depth, exactly as X and Y do.
      // It used to fall straight through to the authored box size, so a
      // `Fill` depth collapsed to whatever number was there (0 on an
      // indefinite axis) while callers offset it as if it were parent-depth.
      if (box.sizeMode.z === 'Fill') {
        child.resolvedSize.z = containingSize.z;
      } else {
        child.resolvedSize.z = box.size.z;
      }

      child.resolvedSize = this.clampSize(child.resolvedSize, box.min, box.max);

      // An authored `inset` is a corner-space placement — the same space a
      // flow child's resolvedPosition is in — so the data edge converts it to
      // the centre-based transform with the one formula it already has, off
      // the sizes THIS solve just produced. Without one, the authored
      // position is canonical and copied through untouched.
      //
      // Presence, not truthiness: a stated 0 is a placement.
      const inset = child.layoutItem?.inset;
      child.resolvedPosition = inset
        ? { x: inset.left ?? 0, y: inset.top ?? 0, z: child.position.z }
        : { ...child.position };
      this.resolve(child, tree, true);
    }
  }

  private resolveNonWrapped(
    node: LayoutNode,
    children: LayoutNode[],
    tree: LayoutTree,
    innerSize: Vector3D,
    mainAxis: LayoutAxis,
    crossA: LayoutAxis,
    crossB: LayoutAxis
  ): void {
    const container = node.layoutContainer!;
    const padding = container.padding;

    this.resolveChildSizes(children, innerSize, mainAxis, crossA, crossB, container.gap, container.alignCrossA, container.alignCrossB);

    let totalMain = 0;
    for (let i = 0; i < children.length; i++) {
      totalMain += getAxisValue(children[i].resolvedSize, mainAxis);
      if (i > 0) totalMain += container.gap;
    }

    const availableMain = getAxisValue(innerSize, mainAxis);
    let mainOffset = this.getStartOffset(padding, mainAxis);
    mainOffset += this.calculateMainStartOffset(container.alignMain, availableMain, totalMain, children.length, container.gap);

    const adjustedGap = this.calculateAdjustedGap(container.alignMain, availableMain, totalMain, children.length, container.gap);

    for (const child of children) {
      const childItem = child.layoutItem;

      let pos = createVector3D();
      pos = setAxisValue(pos, mainAxis, this.seatMain(
        mainAxis, mainOffset, getAxisValue(child.resolvedSize, mainAxis), availableMain, padding
      ));

      const crossAAlign = childItem?.alignSelfCross ?? container.alignCrossA ?? 'Start';
      const crossAOffset = this.calculateCrossOffset(
        crossAAlign,
        getAxisValue(innerSize, crossA),
        getAxisValue(child.resolvedSize, crossA),
        this.getStartOffset(padding, crossA)
      );
      pos = setAxisValue(pos, crossA, crossAOffset);

      const crossBAlign = childItem?.alignSelfCrossB ?? container.alignCrossB ?? 'Start';
      const crossBOffset = this.calculateCrossOffset(
        crossBAlign,
        getAxisValue(innerSize, crossB),
        getAxisValue(child.resolvedSize, crossB),
        this.getStartOffset(padding, crossB)
      );
      pos = setAxisValue(pos, crossB, crossBOffset);

      child.resolvedPosition = pos;
      mainOffset += getAxisValue(child.resolvedSize, mainAxis) + adjustedGap;

      this.resolve(child, tree, true);
    }
  }

  private resolveWrapped(
    node: LayoutNode,
    children: LayoutNode[],
    tree: LayoutTree,
    innerSize: Vector3D,
    mainAxis: LayoutAxis,
    crossA: LayoutAxis,
    crossB: LayoutAxis
  ): void {
    const container = node.layoutContainer!;

    for (const child of children) {
      child.resolvedSize = this.clampSize(
        child.layoutBox.size,
        child.layoutBox.min,
        child.layoutBox.max
      );
    }

    const layers = this.groupIntoLayers(
      children, innerSize, mainAxis, crossA, crossB, container.gap,
      container.crossGap ?? container.gap
    );

    const availableMain = getAxisValue(innerSize, mainAxis);
    const crossAKey = crossA.toLowerCase() as 'x' | 'y' | 'z';
    const crossBKey = crossB.toLowerCase() as 'x' | 'y' | 'z';

    for (const layer of layers) {
      for (const line of layer.lines) {
        this.resolveChildMainAxisSizesOnly(line.children, availableMain, mainAxis, container.gap);

        let mainSize = 0;
        for (let i = 0; i < line.children.length; i++) {
          mainSize += getAxisValue(line.children[i].resolvedSize, mainAxis);
          if (i > 0) mainSize += container.gap;
        }
        line.mainSize = mainSize;

        for (const child of line.children) {
          const item = child.layoutItem;
          const box = child.layoutBox;

          const crossAAlign = item?.alignSelfCross ?? container.alignCrossA;
          if (
            (box.sizeMode[crossAKey] === 'Fill' || (crossAAlign === 'Stretch' && box.sizeMode[crossAKey] !== 'Fixed')) &&
            line.crossASize > 0.0001
          ) {
            child.resolvedSize = setAxisValue(child.resolvedSize, crossA, line.crossASize);
          }

          const crossBAlign = item?.alignSelfCrossB ?? container.alignCrossB;
          if (
            (box.sizeMode[crossBKey] === 'Fill' || (crossBAlign === 'Stretch' && box.sizeMode[crossBKey] !== 'Fixed')) &&
            layer.crossBSize > 0.0001
          ) {
            child.resolvedSize = setAxisValue(child.resolvedSize, crossB, layer.crossBSize);
          }

          child.resolvedSize = this.clampSize(child.resolvedSize, box.min, box.max);
        }
      }
    }

    this.positionLayers(layers, node, innerSize, mainAxis, crossA, crossB, container, tree);
  }

  private groupIntoLayers(
    children: LayoutNode[],
    innerSize: Vector3D,
    mainAxis: LayoutAxis,
    crossA: LayoutAxis,
    _crossB: LayoutAxis,
    gap: number,
    crossGap: number
  ): LayoutLayer[] {
    const availableMain = getAxisValue(innerSize, mainAxis);
    const availableCrossA = getAxisValue(innerSize, crossA);

    const layers: LayoutLayer[] = [];
    let currentLayer: LayoutLayer = { lines: [], crossASize: 0, crossBSize: 0 };
    let currentLine: LayoutLine = { children: [], mainSize: 0, crossASize: 0, crossBSize: 0 };

    for (const child of children) {
      const childMainMeasured = getAxisValue(child.resolvedSize, mainAxis);
      const childCrossASize = getAxisValue(child.resolvedSize, crossA);
      const childCrossBSize = getAxisValue(child.resolvedSize, _crossB);

      const candidateMain =
        currentLine.mainSize + (currentLine.children.length > 0 ? gap : 0) + childMainMeasured;

      if (candidateMain <= availableMain || currentLine.children.length === 0) {
        currentLine.children.push(child);
        currentLine.mainSize = candidateMain;
        currentLine.crossASize = Math.max(currentLine.crossASize, childCrossASize);
        currentLine.crossBSize = Math.max(currentLine.crossBSize, childCrossBSize);
      } else {
        const newCrossASize = currentLayer.crossASize + (currentLayer.lines.length > 0 ? crossGap : 0) + currentLine.crossASize;

        if (newCrossASize <= availableCrossA || currentLayer.lines.length === 0) {
          currentLayer.lines.push(currentLine);
          currentLayer.crossASize = newCrossASize;
          currentLayer.crossBSize = Math.max(currentLayer.crossBSize, currentLine.crossBSize);
        } else {
          layers.push(currentLayer);
          currentLayer = {
            lines: [currentLine],
            crossASize: currentLine.crossASize,
            crossBSize: currentLine.crossBSize
          };
        }

        currentLine = {
          children: [child],
          mainSize: childMainMeasured,
          crossASize: childCrossASize,
          crossBSize: childCrossBSize
        };
      }
    }

    if (currentLine.children.length > 0) {
      currentLayer.lines.push(currentLine);
      currentLayer.crossASize += (currentLayer.lines.length > 1 ? crossGap : 0) + currentLine.crossASize;
      currentLayer.crossBSize = Math.max(currentLayer.crossBSize, currentLine.crossBSize);
    }
    if (currentLayer.lines.length > 0) {
      layers.push(currentLayer);
    }

    return layers;
  }

  private positionLayers(
    layers: LayoutLayer[],
    _node: LayoutNode,
    innerSize: Vector3D,
    mainAxis: LayoutAxis,
    crossA: LayoutAxis,
    crossB: LayoutAxis,
    container: NonNullable<LayoutNode['layoutContainer']>,
    tree: LayoutTree
  ): void {
    const padding = container.padding;
    const alignContentCrossA = container.alignContentCrossA ?? 'Start';
    const alignContentCrossB = container.alignContentCrossB ?? 'Start';
    const crossGap = container.crossGap ?? container.gap;

    let totalCrossB = 0;
    for (let i = 0; i < layers.length; i++) {
      totalCrossB += layers[i].crossBSize;
      if (i > 0) totalCrossB += crossGap;
    }

    const availableCrossB = getAxisValue(innerSize, crossB);
    let crossBOffset = this.getStartOffset(padding, crossB);
    crossBOffset += this.calculateMainStartOffset(
      alignContentCrossB, availableCrossB, totalCrossB, layers.length, crossGap
    );
    const adjustedGapCrossB = this.calculateAdjustedGap(
      alignContentCrossB, availableCrossB, totalCrossB, layers.length, crossGap
    );

    for (const layer of layers) {
      let totalCrossA = 0;
      for (let i = 0; i < layer.lines.length; i++) {
        totalCrossA += layer.lines[i].crossASize;
        if (i > 0) totalCrossA += crossGap;
      }

      const availableCrossA = getAxisValue(innerSize, crossA);
      let crossAOffset = this.getStartOffset(padding, crossA);
      crossAOffset += this.calculateMainStartOffset(
        alignContentCrossA, availableCrossA, totalCrossA, layer.lines.length, crossGap
      );
      const adjustedGapCrossA = this.calculateAdjustedGap(
        alignContentCrossA, availableCrossA, totalCrossA, layer.lines.length, crossGap
      );

      for (const line of layer.lines) {
        const availableMain = getAxisValue(innerSize, mainAxis);
        let mainOffset = this.getStartOffset(padding, mainAxis);
        mainOffset += this.calculateMainStartOffset(container.alignMain, availableMain, line.mainSize, line.children.length, container.gap);

        const adjustedGap = this.calculateAdjustedGap(container.alignMain, availableMain, line.mainSize, line.children.length, container.gap);

        for (const child of line.children) {
          const childItem = child.layoutItem;

          const crossAAlign = childItem?.alignSelfCross ?? container.alignCrossA ?? 'Start';
          const crossBAlign = childItem?.alignSelfCrossB ?? container.alignCrossB ?? 'Start';

          const childCrossA = this.calculateCrossOffset(
            crossAAlign, line.crossASize, getAxisValue(child.resolvedSize, crossA), 0
          );
          const childCrossB = this.calculateCrossOffset(
            crossBAlign, layer.crossBSize, getAxisValue(child.resolvedSize, crossB), 0
          );

          let pos = createVector3D();
          pos = setAxisValue(pos, mainAxis, this.seatMain(
            mainAxis, mainOffset, getAxisValue(child.resolvedSize, mainAxis), availableMain, padding
          ));
          pos = setAxisValue(pos, crossA, crossAOffset + childCrossA);
          pos = setAxisValue(pos, crossB, crossBOffset + childCrossB);

          child.resolvedPosition = pos;
          mainOffset += getAxisValue(child.resolvedSize, mainAxis) + adjustedGap;

          this.resolve(child, tree, true);
        }

        crossAOffset += line.crossASize + adjustedGapCrossA;
      }

      crossBOffset += layer.crossBSize + adjustedGapCrossB;
    }
  }

  // === HELPER METHODS ===

  private resolveChildSizes(
    children: LayoutNode[],
    innerSize: Vector3D,
    mainAxis: LayoutAxis,
    crossA: LayoutAxis,
    crossB: LayoutAxis,
    gap: number,
    containerAlignCrossA?: string,
    containerAlignCrossB?: string
  ): void {
    const availableMain = getAxisValue(innerSize, mainAxis);

    const { flexes, childFlexMap } = this.buildFlexInputs(children, mainAxis, availableMain);

    const targetMain = distributeFlex(flexes, availableMain, gap);

    for (const f of flexes) {
      const { child, minMain, maxMain } = childFlexMap.get(f.id)!;
      const item = child.layoutItem;
      const box = child.layoutBox;

      let resolved = { ...box.size };
      const main = clampFlexMain(targetMain.get(child.id) ?? f.baseMain, minMain, maxMain);
      resolved = setAxisValue(resolved, mainAxis, main);

      // Cross axis A
      const crossAKey = crossA.toLowerCase() as 'x' | 'y' | 'z';
      const availCrossA = getAxisValue(innerSize, crossA);
      const relativeCrossAPercent = this.getRelativeAxisPercent(child, crossA);
      const effectiveCrossA = item?.alignSelfCross ?? containerAlignCrossA;
      if (relativeCrossAPercent != null) {
        resolved = setAxisValue(resolved, crossA, availCrossA * (relativeCrossAPercent / 100));
      } else if (
        box.sizeMode[crossAKey] === 'Fill' ||
        (effectiveCrossA === 'Stretch' && box.sizeMode[crossAKey] !== 'Fixed')
      ) {
        if (availCrossA > 0.0001) {
          resolved = setAxisValue(resolved, crossA, availCrossA);
        }
      }

      // Cross axis B
      const crossBKey = crossB.toLowerCase() as 'x' | 'y' | 'z';
      const availCrossB = getAxisValue(innerSize, crossB);
      const relativeCrossBPercent = this.getRelativeAxisPercent(child, crossB);
      const effectiveCrossB = item?.alignSelfCrossB ?? containerAlignCrossB;
      if (relativeCrossBPercent != null) {
        resolved = setAxisValue(resolved, crossB, availCrossB * (relativeCrossBPercent / 100));
      } else if (
        box.sizeMode[crossBKey] === 'Fill' ||
        (effectiveCrossB === 'Stretch' && box.sizeMode[crossBKey] !== 'Fixed')
      ) {
        if (availCrossB > 0.0001) {
          resolved = setAxisValue(resolved, crossB, availCrossB);
        }
      }

      child.resolvedSize = this.clampSize(resolved, box.min, box.max);
    }
  }

  private resolveChildMainAxisSizesOnly(
    children: LayoutNode[],
    availableMain: number,
    mainAxis: LayoutAxis,
    gap: number
  ): void {
    const axisKey = mainAxis.toLowerCase() as 'x' | 'y' | 'z';
    const flexes: FlexChild[] = [];

    for (const child of children) {
      const item = child.layoutItem;
      const box = child.layoutBox;
      const relativeMainPercent = this.getRelativeAxisPercent(child, mainAxis);
      const mainMode = relativeMainPercent != null ? 'Fixed' : (item?.mainAxisMode ?? box.sizeMode[axisKey]);

      const basisOverride = item?.flexBasis ?? null;
      const measuredMain =
        relativeMainPercent != null
          ? (availableMain * relativeMainPercent) / 100
          : getAxisValue(child.resolvedSize, mainAxis);
      const baseMain =
        relativeMainPercent != null
          ? measuredMain
          : (basisOverride != null ? basisOverride : (mainMode === 'Fill' ? 0 : measuredMain));
      const grow = item?.flexGrow ?? (mainMode === 'Fill' ? 1 : 0);
      const shrink = item?.flexShrink ?? 1;

      const explicitMin = getAxisValue(box.min, mainAxis);
      const minExplicit = mainAxis === 'X' ? box.minExplicitX : mainAxis === 'Y' ? box.minExplicitY : box.minExplicitZ;
      const hasClipContent = child.layoutContainer?.clipContent === true;
      const minMain = computeFlexMin(
        mainMode, explicitMin, measuredMain, !!minExplicit, hasClipContent,
        this.hasMeasuredContentFloor(child, mainAxis)
      );

      flexes.push({
        id: child.id, baseMain, grow: Math.max(0, grow), shrink: Math.max(0, shrink),
        minMain, maxMain: getAxisValue(box.max, mainAxis)
      });
    }

    const targetMain = distributeFlex(flexes, availableMain, gap);

    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const f = flexes[i];
      const box = child.layoutBox;
      const main = Math.min(f.maxMain, Math.max(f.minMain, targetMain.get(child.id) ?? f.baseMain));
      child.resolvedSize = this.clampSize(
        setAxisValue(child.resolvedSize, mainAxis, main),
        box.min, box.max
      );
    }
  }

  /** The one flow child a content slot fits, or null when no fit applies. */
  private fittedChild(node: LayoutNode, flowChildren: LayoutNode[]): LayoutNode | null {
    const container = node.layoutContainer;
    if (!container?.contentFit || container.wrap === true || flowChildren.length !== 1) return null;
    return flowChildren[0];
  }

  /** A child's box as its parent's flow sees it: times its fit scale, if any. */
  private flowBox(child: LayoutNode, size: Vector3D): Vector3D {
    const s = child.flowScale;
    return s === undefined ? size : { x: size.x * s, y: size.y * s, z: size.z * s };
  }

  /**
   * Seat a content slot's child. The child keeps its own box at scale 1 —
   * its children resolve inside that — and is placed by its fitted box,
   * with the slot's alignment. The fit owns the child's size, so it is never
   * stretched or filled.
   */
  private resolveFittedChild(node: LayoutNode, child: LayoutNode, tree: LayoutTree, innerSize: Vector3D): void {
    const container = node.layoutContainer!;
    const padding = container.padding;
    const [crossA, crossB] = getContainerCrossAxes(container);
    const mainAxis = container.directionAxis;
    const box = child.layoutBox;

    child.resolvedSize = this.clampSize(box.size, box.min, box.max);
    child.flowScale = computeContentFitScale(container.contentFit!, child.resolvedSize, {
      x: node.layoutBox.sizeMode.x === 'Hug' ? null : innerSize.x,
      y: node.layoutBox.sizeMode.y === 'Hug' ? null : innerSize.y,
    });
    const outer = this.flowBox(child, child.resolvedSize);

    const availableMain = getAxisValue(innerSize, mainAxis);
    const outerMain = getAxisValue(outer, mainAxis);
    const mainOffset = this.getStartOffset(padding, mainAxis)
      + this.calculateMainStartOffset(container.alignMain, availableMain, outerMain, 1, container.gap);

    let pos = createVector3D();
    pos = setAxisValue(pos, mainAxis, this.seatMain(mainAxis, mainOffset, outerMain, availableMain, padding));
    pos = setAxisValue(pos, crossA, this.calculateCrossOffset(
      child.layoutItem?.alignSelfCross ?? container.alignCrossA ?? 'Start',
      getAxisValue(innerSize, crossA), getAxisValue(outer, crossA), this.getStartOffset(padding, crossA),
    ));
    pos = setAxisValue(pos, crossB, this.calculateCrossOffset(
      child.layoutItem?.alignSelfCrossB ?? container.alignCrossB ?? 'Start',
      getAxisValue(innerSize, crossB), getAxisValue(outer, crossB), this.getStartOffset(padding, crossB),
    ));
    child.resolvedPosition = pos;

    this.resolve(child, tree, true);
  }

  /**
   * Min-WINS when min > max (the CSS rule): clamp to max first, then floor
   * by min. The tree parser already resolves authored min/max conflicts
   * min-wins, but `measureLeaf` can re-create min>max after parsing (an
   * intrinsic min above an authored max) — and a max-wins clamp would then
   * leave resolvedSize below min, tripping the assertMinSize invariant and
   * aborting the pass. Behavior-identical whenever min <= max.
   */
  private clampSize(size: Vector3D, min: Vector3D, max: Vector3D): Vector3D {
    return {
      x: Math.max(min.x, Math.min(max.x, size.x)),
      y: Math.max(min.y, Math.min(max.y, size.y)),
      z: Math.max(min.z, Math.min(max.z, size.z))
    };
  }

  private getInnerSize(size: Vector3D, padding: NonNullable<LayoutNode['layoutContainer']>['padding']): Vector3D {
    return {
      x: Math.max(0, size.x - padding.left - padding.right),
      y: Math.max(0, size.y - padding.top - padding.bottom),
      z: Math.max(0, size.z - padding.front - padding.back),
    };
  }

  private getMainPadding(axis: LayoutAxis, padding: NonNullable<LayoutNode['layoutContainer']>['padding']): number {
    switch (axis) {
      case 'X': return padding.left + padding.right;
      case 'Y': return padding.top + padding.bottom;
      case 'Z': return padding.front + padding.back;
    }
  }

  private getStartOffset(padding: NonNullable<LayoutNode['layoutContainer']>['padding'], axis: LayoutAxis): number {
    switch (axis) {
      case 'X': return padding.left;
      case 'Y': return padding.top;
      case 'Z': return padding.front;
    }
  }

  /**
   * Seat a child's main-axis offset, mirroring it when the main axis is depth.
   *
   * Layout space counts depth from the container's back face: `getStartOffset`
   * hands a Z main axis `padding.front`, and `alignMain: 'Start'` puts the first
   * child at z = 0. Read plainly that is a stack advancing toward +Z — and the
   * scene this solves for is right-handed with the camera at +Z
   * (`SceneCamera.setupCamera`), so the first child would land furthest from the
   * viewer and every later one in front of it. Layouts are authored for the
   * opposite picture: a stack runs away from the viewer, first child nearest. A
   * device frame listed after its UI must sit behind the UI; ascending offsets
   * put it in front, closing over the UI like a lid.
   *
   * Mirroring the offset within the container's own depth gives the authored
   * picture, and only for a depth stack. `resolvedPosition.z` still counts from
   * the back face, so `behaviors/layout.ts`'s one frame conversion needs no
   * change; what moves is that `alignMain: 'Start'` and `padding.front` now name
   * the face the camera is on. `direction: 'Z'` is documented that way in the
   * Layout schema and in LAYOUT_ENGINE.md §1.
   *
   * The cross axes are deliberately untouched. A model's `forward` seat is
   * already mapped onto `alignCrossB: 'End'` by whoever authors it, and
   * `getCrossAxes('Z')` is `['X', 'Y']`, so a depth stack has no depth cross
   * axis to disturb.
   */
  private seatMain(
    mainAxis: LayoutAxis,
    offset: number,
    childMain: number,
    availableMain: number,
    padding: NonNullable<LayoutNode['layoutContainer']>['padding']
  ): number {
    if (mainAxis !== 'Z') return offset;
    // Mirror within the outer box (content span plus both paddings), not the
    // content box: that is what carries `padding.front` over to the front.
    return padding.front + availableMain + padding.back - offset - childMain;
  }

  private calculateMainStartOffset(align: MainAlignment, available: number, used: number, childCount: number, gap: number): number {
    switch (align) {
      case 'Start': return 0;
      case 'Center': return (available - used) / 2;
      case 'End': return available - used;
      case 'SpaceBetween': return 0;
      case 'SpaceAround': return childCount > 0 ? (available - used + gap * (childCount - 1)) / (childCount * 2) : 0;
      case 'SpaceEvenly': {
        const contentSize = used - gap * (childCount - 1);
        return childCount > 0 ? (available - contentSize) / (childCount + 1) : 0;
      }
    }
  }

  private calculateAdjustedGap(align: MainAlignment, available: number, used: number, childCount: number, gap: number): number {
    if (childCount <= 1) return gap;

    switch (align) {
      case 'SpaceBetween': {
        const contentSize = used - gap * (childCount - 1);
        return (available - contentSize) / (childCount - 1);
      }
      case 'SpaceAround': {
        const contentSize = used - gap * (childCount - 1);
        return (available - contentSize) / childCount;
      }
      case 'SpaceEvenly': {
        const contentSize = used - gap * (childCount - 1);
        return (available - contentSize) / (childCount + 1);
      }
      default:
        return gap;
    }
  }

  private calculateCrossOffset(align: LayoutAlignment, available: number, childSize: number, paddingStart: number): number {
    switch (align) {
      case 'Start': return paddingStart;
      case 'Center': return paddingStart + (available - childSize) / 2;
      case 'End': return paddingStart + available - childSize;
      case 'Stretch': return paddingStart;
    }
  }
}
